import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ScheduleDemoModal from '../components/ScheduleDemoModal';
import { downloadCollegeBrochure } from '../utils/downloadHelpers';
import { 
  BookOpen, 
  Users, 
  Presentation, 
  Briefcase, 
  Target, 
  MessageSquare,
  TrendingUp,
  Building2,
  Network,
  Award,
  CheckCircle2,
  Download,
  Calendar
} from 'lucide-react';
import { 
  heroMetrics, 
  offerings, 
  benefits, 
  partnershipModel, 
  longTermValue 
} from '../mock';

const Home = () => {
  const offeringIcons = [BookOpen, Presentation, Users, Briefcase, Target, MessageSquare];
  const benefitIcons = [TrendingUp, Building2, Target, CheckCircle2, Award];
  const valueIcons = [TrendingUp, Building2, Network, Award];
  
  const [showDemoModal, setShowDemoModal] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>EduSocial - Bridge the Gap Between Classroom & Career</title>
        <meta name="description" content="Partner with EduSocial to establish a Centre of Excellence at your institution. 100+ industry professionals, 25+ partner colleges, 100% placement assistance." />
        <meta property="og:title" content="EduSocial - Bridge the Gap Between Classroom & Career" />
        <meta property="og:description" content="Industry-integrated learning ecosystems for educational institutions across India." />
        <link rel="canonical" href="https://edusocial.co.in" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(135deg, #fffaf1 0%, #fff5e1 100%)'}}>
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{color: '#012726'}}>
                Bridge the Gap Between{' '}
                <span style={{color: '#33c375'}}>Classroom & Career</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Partner with EduSocial to establish a Centre of Excellence at your institution. 
                Empower students through structured mentorship, industry workshops, and real-world projects.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg"
                  style={{backgroundColor: '#33c375'}}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2ab066'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#33c375'}
                  onClick={() => setShowDemoModal(true)}
                >
                  Partner With Us
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="px-8 py-6 text-lg transition-all duration-200"
                  style={{borderColor: '#012726', borderWidth: '2px', color: '#012726'}}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f0f9f4';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                  onClick={downloadCollegeBrochure}
                >
                  <Download className="mr-2" size={20} />
                  Download Brochure
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c" 
                alt="Students collaborating" 
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16">
            {heroMetrics.map((metric, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-4xl sm:text-5xl font-bold mb-2" style={{color: '#33c375'}}>
                  {metric.number}
                </div>
                <div className="text-gray-600 font-medium">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Industry-Integrated Learning Ecosystem
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A comprehensive platform connecting students with real industry expertise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((offering, index) => {
              const Icon = offeringIcons[index];
              return (
                <Card 
                  key={offering.id} 
                  className="border-2 hover:shadow-lg transition-all duration-300 group bg-white"
                  style={{'--hover-border': '#33c375'}}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#33c375'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}
                >
                  <CardContent className="p-6 space-y-4">
                    <div 
                      className="w-14 h-14 rounded-lg flex items-center justify-center transition-colors duration-300"
                      style={{backgroundColor: '#e8f5ee'}}
                    >
                      <Icon style={{color: '#33c375'}} size={28} />
                    </div>
                    <h3 className="text-xl font-semibold" style={{color: '#012726'}}>
                      {offering.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {offering.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              What It Solves for Your Institution
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefitIcons[index];
              return (
                <div key={benefit.id} className="space-y-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{backgroundColor: '#33c375'}}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold" style={{color: '#012726'}}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Model Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A simple, structured approach to building your Centre of Excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* EduSocial Provides */}
            <Card className="border-2 bg-white" style={{borderColor: '#e8f5ee', backgroundColor: '#f9fefb'}}>
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 text-white rounded-lg flex items-center justify-center font-bold text-lg" style={{backgroundColor: '#33c375'}}>
                    01
                  </div>
                  <h3 className="text-2xl font-bold" style={{color: '#012726'}}>
                    EduSocial Provides
                  </h3>
                </div>
                <ul className="space-y-3">
                  {partnershipModel.eduSocialProvides.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 style={{color: '#33c375'}} className="flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* College Provides */}
            <Card className="border-2 border-gray-200 bg-white">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 text-white rounded-lg flex items-center justify-center font-bold text-lg" style={{backgroundColor: '#012726'}}>
                    02
                  </div>
                  <h3 className="text-2xl font-bold" style={{color: '#012726'}}>
                    College Provides
                  </h3>
                </div>
                <ul className="space-y-3">
                  {partnershipModel.collegeProvides.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="text-gray-700 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Badge variant="outline" className="px-4 py-2 text-sm" style={{borderColor: '#33c375', color: '#012726'}}>
              <Calendar className="mr-2" size={16} />
              Sessions conducted periodically throughout the semester
            </Badge>
          </div>
        </div>
      </section>

      {/* Long-term Value Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Sustainable Impact on Your Institution
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {longTermValue.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <div key={value.id} className="text-center space-y-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto transform hover:scale-110 transition-transform duration-300" 
                    style={{backgroundColor: '#33c375'}}
                  >
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold" style={{color: '#012726'}}>
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 text-white" style={{background: 'linear-gradient(135deg, #012726 0%, #014d49 100%)'}}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Build Your Centre of Excellence?
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            Join 25+ partner institutions transforming student outcomes through industry-integrated learning
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg"
              style={{backgroundColor: '#33c375'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2ab066'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#33c375'}
              onClick={() => setShowDemoModal(true)}
            >
              Schedule a Demo
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 text-white px-8 py-6 text-lg transition-all duration-200"
              style={{borderColor: 'white'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              onClick={downloadCollegeBrochure}
            >
              <Download className="mr-2" size={20} />
              Download Partnership Details
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Badge className="px-4 py-2 text-sm transition-colors" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              No upfront costs
            </Badge>
            <Badge className="px-4 py-2 text-sm transition-colors" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              Flexible scheduling
            </Badge>
            <Badge className="px-4 py-2 text-sm transition-colors" style={{backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)'}}>
              Proven results
            </Badge>
          </div>
        </div>
      </section>
      
      <ScheduleDemoModal open={showDemoModal} onOpenChange={setShowDemoModal} />
    </div>
  );
};

export default Home;
